import * as React from 'react';
import moment from 'moment';
import { Input } from 'reactstrap';
import StyledTable from '../StyledTable';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import styled from 'styled-components';
import { TaskItemData, TaskItemStatus, UserData, LoggedUser } from '../../models';
import { ClientEntity } from '../../store/entities/state';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import {
    canUpdateTask,
    getTaskStatusTranslationKey,
    getTaskTypeIcon,
    getTaskTypeTranslationKey,
    isTaskOverdue,
} from '../../pages/Tasks/taskUtils';

export interface TaskRowEditState {
    taskId: string;
    clientId?: string;
    assignedUserId?: string;
    title: string;
    dueDate: string;
}

interface TaskItemsTableProps {
    tasks: TaskItemData[];
    user?: LoggedUser;
    canManageVisibleTasks?: boolean;
    isLoading?: boolean;
    emptyStateLabel: string;
    showClientColumn?: boolean;
    showResponsibleColumn?: boolean;
    allowInlineEdit?: boolean;
    allowClientEdit?: boolean;
    allowAssignedUserEdit?: boolean;
    clients?: ClientEntity[];
    users?: UserData[];
    rowEdit?: TaskRowEditState | null;
    setRowEdit?: React.Dispatch<React.SetStateAction<TaskRowEditState | null>>;
    isSavingRow?: boolean;
    editingRowRef?: React.RefObject<HTMLTableRowElement>;
    onStartEdit?: (task: TaskItemData) => void;
    onSaveEdit?: (task: TaskItemData) => void | Promise<void>;
    onCancelEdit?: () => void;
    onCompleteTask?: (task: TaskItemData) => void;
    onDismissTask?: (task: TaskItemData) => void;
    onNavigateClient?: (clientId?: string) => void;
    onNavigateProfessional?: (professionalId?: string) => void;
}

const StyledTasksTable = styled(StyledTable)`
    && {
        min-width: 760px;
        width: 100%;

        th,
        td {
            white-space: nowrap;
        }

        th:first-child,
        td:first-child {
            min-width: 260px;
        }
    }
`;

const TaskTypeMeta = styled.div`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const TaskTypeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 999px;
    flex-shrink: 0;
    color: var(--brand-secondary);
    background: var(--surface-brand-soft);

    svg {
        fill: currentColor;
        font-size: 1.05rem;
    }
`;

const TaskTitle = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-strong);
    line-height: 1.35;
    white-space: nowrap;
`;

const StatusBadge = styled.span<{ $status: TaskItemStatus }>`
    display: inline-flex;
    align-items: center;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
    color: ${({ $status }) => {
        if ($status === TaskItemStatus.Completed) return 'var(--success-color)';
        if ($status === TaskItemStatus.Dismissed) return 'var(--text-muted)';
        return 'var(--warning-color-strong, var(--warning-color))';
    }};
    background: ${({ $status }) => {
        if ($status === TaskItemStatus.Completed) return 'var(--success-bg)';
        if ($status === TaskItemStatus.Dismissed) return 'var(--surface-brand-soft)';
        return 'var(--warning-bg)';
    }};
`;

const SecondaryCellText = styled.span<React.HTMLAttributes<HTMLSpanElement>>`
    display: inline-flex;
    align-items: center;
    min-height: 1.9rem;
    font-size: 0.85rem;
    font-weight: 400;
    color: var(--text-muted);
    line-height: 1.35;
`;

const DueDateText = styled(SecondaryCellText)<React.HTMLAttributes<HTMLSpanElement> & { $tone: 'default' | 'today' | 'overdue' }>`
    color: ${({ $tone }) => {
        if ($tone === 'overdue') return 'var(--danger-color-strong)';
        if ($tone === 'today') return 'var(--warning-color-strong)';
        return 'var(--text-muted)';
    }};
`;

const EntityLinkButton = styled.button<React.ButtonHTMLAttributes<HTMLButtonElement>>`
    border: 0;
    padding: 0;
    background: none;
    color: var(--text-muted);
    font-size: 0.85rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: left;

    &:hover,
    &:focus {
        color: var(--brand-secondary);
        text-decoration: none;
    }
`;

const RowActionGroup = styled.td`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XXS);
`;


const TaskItemsTable: React.FC<TaskItemsTableProps> = ({
    tasks,
    user,
    canManageVisibleTasks = false,
    isLoading = false,
    emptyStateLabel,
    showClientColumn = true,
    showResponsibleColumn = true,
    allowInlineEdit = false,
    allowClientEdit = true,
    allowAssignedUserEdit = true,
    clients = [],
    users = [],
    rowEdit = null,
    setRowEdit,
    isSavingRow = false,
    editingRowRef,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onCompleteTask,
    onDismissTask,
    onNavigateClient,
    onNavigateProfessional,
}) => {
    const t = useTranslations();
    const colSpan = 4 + (showClientColumn ? 1 : 0) + (showResponsibleColumn ? 1 : 0);

    return (
        <>
            <StyledTasksTable scrollable hover>
                    <thead>
                        <tr>
                            <th>{t(TK.task)}</th>
                            <th>{t(TK.dueDate)}</th>
                            {showClientColumn && <th>{t(TK.client)}</th>}
                            {showResponsibleColumn && <th>{t(TK.responsible)}</th>}
                            <th>{t(TK.status)}</th>
                            <th aria-label={t(TK.action)} />
                        </tr>
                    </thead>
                    <tbody>
                        {!tasks.length && (
                            <tr>
                                <td colSpan={colSpan}>{emptyStateLabel}</td>
                            </tr>
                        )}
                        {tasks.map(task => {
                        const canEditTask = canUpdateTask(task, user, canManageVisibleTasks);
                        const canInlineEditTask = allowInlineEdit && canEditTask && task.status === TaskItemStatus.Pending;
                        const isEditing = rowEdit?.taskId === task.id;
                        const overdue = isTaskOverdue(task);
                        const isDueToday = task.status === TaskItemStatus.Pending && moment(task.dueDate).isSame(moment(), 'day');
                        const TaskIcon = getTaskTypeIcon(task.type);
                        const rowStyle = {
                            ...(canInlineEditTask && !isEditing ? { cursor: 'pointer' } : {}),
                        };
                        const dueDateTone = overdue ? 'overdue' : isDueToday ? 'today' : 'default';

                        return (
                            <tr
                                key={task.id}
                                ref={isEditing ? editingRowRef : undefined}
                                style={rowStyle}
                                onClick={canInlineEditTask && !isEditing ? () => onStartEdit?.(task) : undefined}
                            >
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                        <TaskTypeMeta>
                                            <TaskTypeIcon>
                                                <TaskIcon fontSize="small" />
                                            </TaskTypeIcon>
                                        </TaskTypeMeta>
                                        {isEditing ? (
                                            <Input
                                                bsSize="sm"
                                                value={rowEdit?.title ?? ''}
                                                onChange={event => setRowEdit?.(prev => prev
                                                    ? { ...prev, title: event.target.value }
                                                    : prev)}
                                            />
                                        ) : (
                                            <TaskTitle>{task.title || t(getTaskTypeTranslationKey(task.type))}</TaskTitle>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    {isEditing ? (
                                        <Input
                                            type="datetime-local"
                                            bsSize="sm"
                                            value={rowEdit?.dueDate ?? ''}
                                            onChange={event => setRowEdit?.(prev => prev
                                                ? { ...prev, dueDate: event.target.value }
                                                : prev)}
                                        />
                                    ) : (
                                        <DueDateText $tone={dueDateTone}>{moment(task.dueDate).format('MMM D, HH:mm')}</DueDateText>
                                    )}
                                </td>
                                {showClientColumn && (
                                    <td>
                                        {isEditing && allowClientEdit ? (
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={rowEdit?.clientId ?? ''}
                                                onChange={event => setRowEdit?.(prev => prev
                                                    ? { ...prev, clientId: event.target.value || undefined }
                                                    : prev)}
                                            >
                                                <option value="">{t(TK.none)}</option>
                                                {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
                                            </Input>
                                        ) : (
                                            task.clientId && task.clientName && onNavigateClient ? (
                                                <EntityLinkButton
                                                    type="button"
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        onNavigateClient(task.clientId);
                                                    }}
                                                >
                                                    {task.clientName}
                                                </EntityLinkButton>
                                            ) : (
                                                <SecondaryCellText>{task.clientName || '-'}</SecondaryCellText>
                                            )
                                        )}
                                    </td>
                                )}
                                {showResponsibleColumn && (
                                    <td>
                                        {isEditing && allowAssignedUserEdit ? (
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={rowEdit?.assignedUserId ?? ''}
                                                onChange={event => setRowEdit?.(prev => prev
                                                    ? { ...prev, assignedUserId: event.target.value || undefined }
                                                    : prev)}
                                            >
                                                <option value="">{t(TK.none)}</option>
                                                {users.map(scopeUser => (
                                                    <option key={scopeUser.id} value={scopeUser.id}>
                                                        {scopeUser.name || scopeUser.username}
                                                    </option>
                                                ))}
                                            </Input>
                                        ) : (
                                            task.assignedProfessionalId && task.assignedUserName && onNavigateProfessional ? (
                                                <EntityLinkButton
                                                    type="button"
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        onNavigateProfessional(task.assignedProfessionalId);
                                                    }}
                                                >
                                                    {task.assignedUserName}
                                                </EntityLinkButton>
                                            ) : (
                                                <SecondaryCellText>{task.assignedUserName || '-'}</SecondaryCellText>
                                            )
                                        )}
                                    </td>
                                )}
                                <td>
                                    <StatusBadge $status={task.status}>{t(getTaskStatusTranslationKey(task.status))}</StatusBadge>
                                </td>
                                <RowActionGroup>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title={t(TK.save)}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isSavingRow}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            void onSaveEdit?.(task);
                                                        }}
                                                        style={{ color: 'var(--success-color)' }}
                                                    >
                                                        <CheckCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t(TK.cancel)}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isSavingRow}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            onCancelEdit?.();
                                                        }}
                                                        style={{ color: 'var(--danger-color)' }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            {canInlineEditTask && (
                                                <Tooltip title={t(TK.edit)}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                onStartEdit?.(task);
                                                            }}
                                                            style={{ color: 'var(--text-muted)' }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {canEditTask && task.status === TaskItemStatus.Pending && (
                                                <>
                                                    <Tooltip title={t(TK.completeTask)}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={event => {
                                                                    event.stopPropagation();
                                                                    onCompleteTask?.(task);
                                                                }}
                                                                style={{ color: 'var(--success-color)' }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={t(TK.dismissTask)}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={event => {
                                                                    event.stopPropagation();
                                                                    onDismissTask?.(task);
                                                                }}
                                                                style={{ color: 'var(--danger-color)' }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {(task.status === TaskItemStatus.Completed || task.status === TaskItemStatus.Dismissed) && (task.notes || task.dismissReason || task.completedAt) && (
                                                <Tooltip
                                                    title={
                                                        <span style={{ whiteSpace: 'pre-line' }}>
                                                            {task.completedAt && `${t(TK.completedAt)}: ${moment(task.completedAt).format('MMM D, HH:mm')}\n`}
                                                            {task.status === TaskItemStatus.Dismissed && task.dismissReason && `${t(TK.dismissReason)}: ${task.dismissReason}\n`}
                                                            {task.notes && `${t(TK.notes)}: ${task.notes}`}
                                                        </span>
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={event => event.stopPropagation()}
                                                            style={{ color: 'var(--text-muted)' }}
                                                        >
                                                            <InfoOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </RowActionGroup>
                            </tr>
                        );
                        })}
                    </tbody>
                </StyledTasksTable>

            {isLoading && <div>{t(TK.loading)}</div>}
        </>
    );
};

export default TaskItemsTable;
